import { ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP } from '@siliconx/shared';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { AvoidedEventView } from '../../../hooks/useEnvironmentalOverview';
import { formatTonnes } from '../format';

/**
 * SIL-476 — "Second-life ledger" (per
 * `Environmental Fleet Detail.dc.html`). Deliberately reuses the Overview
 * tab's avoided-events data (`useAvoidedEventsTape`) rather than re-querying
 * — it IS the same per-transaction resale data (SIL-471), presented as a
 * ledger instead of a tape, so the two tabs never disagree about which
 * transfers happened or which are verified.
 *
 * Per the SIL-459 register R6: "Verified" is a per-transfer attestation that
 * the TRANSACTION happened and is passport-matched on the platform — never a
 * claim about the emissions estimate next to it. `event.verified` is true
 * ONLY for a real row with a linked passport (never true for a sample row,
 * which has no transaction to verify) — see
 * `ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP`'s exact wording, shown verbatim on
 * hover, never reworded for this tab.
 */
export function SecondLifeLedger({
  events,
  totalAvoidedKgco2e,
}: {
  events: AvoidedEventView[];
  totalAvoidedKgco2e: number;
}) {
  if (events.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-zinc-400">No second-life transfers recorded yet.</div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mb-1.5 text-right font-mono text-xs font-semibold text-emerald-700">
        −{formatTonnes(totalAvoidedKgco2e)} avoided across {events.length} transfer
        {events.length === 1 ? '' : 's'}
      </div>
      {events.map((e) => (
        <div key={e.id} className="flex items-center gap-3.5 border-b border-zinc-100 py-2 text-sm last:border-0">
          <span className="w-20 shrink-0 truncate font-mono text-[13px] text-emerald-700">{e.id.slice(0, 8)}</span>
          <span className="flex-1 truncate">{e.model}</span>
          {e.verified && (
            <StatusPill tone="green" title={ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP} className="shrink-0">
              Verified
            </StatusPill>
          )}
          <span className="w-20 shrink-0 text-right font-mono text-sm font-semibold tabular-nums text-emerald-700">
            −{formatTonnes(e.avoidedKgco2e)}
          </span>
        </div>
      ))}
    </div>
  );
}
